import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchUserData } from '../../../../Utilities/api/index.js';
import { BackButton } from '../../../../Utilities/components/Buttons/index.js';
import './PaymentHistory.css';

function toSafeLower(str) {
  if (!str) return '';
  return String(str).toLowerCase();
}

function formatMoney(n) {
  var num = parseFloat(n);
  if (isNaN(num)) num = 0;
  return '$' + num.toFixed(2);
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString();
  } catch (e) {
    return 'N/A';
  }
}

const UserPaymentHistory = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all'); // 'all' | 'past' | 'upcoming'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(function loadHistory() {
    var mounted = true;
    async function fetchPaymentHistory() {
      setLoading(true);
      setError(null);
      try {
        const userData = await fetchUserData(userId);
        var list = [];

        if (userData) {
          var bookings = Array.isArray(userData.Bookings) ? userData.Bookings : [];
          var memberships = Array.isArray(userData.ClubMemberships) ? userData.ClubMemberships : [];
          var bundles = Array.isArray(userData.DaycareBundles) ? userData.DaycareBundles : [];
          var transactions = Array.isArray(userData.CustomerTransactions) ? userData.CustomerTransactions : [];

          // Normalize them into one shape
          list = [
            ...bookings.map(b => ({ type: 'booking', ...b })),
            ...memberships.map(m => ({ type: 'membership', ...m })),
            ...bundles.map(bu => ({ type: 'bundle', ...bu })),
            ...transactions.map(t => ({ type: 'transaction', ...t })),
          ];
        }

        setTransactions(list);
        if (mounted) setTransactions(list);
      } catch (err) {
        if (mounted) {
          setError(err && err.message ? err.message : 'Failed to fetch payment history');
          setTransactions([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchPaymentHistory();
    return function cleanup(){ mounted = false; };
  }, [userId]);

  function handleSearchChange(e) {
    setSearch(e.target.value);
    setCurrentPage(1);
  }

  function handleToggleView(newView) {
    setView(newView);
    setCurrentPage(1);
  }

  // Filtering
  var q = search.toLowerCase();
  var filteredTransactions = (transactions || []).filter(function (t) {
    if (!t) return false;

    var petsMatch = false;
    if (t.selectedPets && Array.isArray(t.selectedPets)) {
      petsMatch = t.selectedPets.some(function (pet) {
        var name = pet && pet.petName ? String(pet.petName) : '';
        return name.toLowerCase().indexOf(q) !== -1;
      });
    }

    var idMatch = t.id && String(t.id).indexOf(search) !== -1;
    var dateStr = t.startTime ? formatDate(t.startTime) : '';
    var dateMatch = dateStr.toLowerCase().indexOf(q) !== -1;

    var status = t.transactionStatus ? String(t.transactionStatus) : '';
    var statusMatch = status.toLowerCase().indexOf(q) !== -1;

    var priceMatch = t.originalPrice && String(t.originalPrice).indexOf(search) !== -1;

    var type = t.bookingType ? String(t.bookingType) : '';
    var typeMatch = type.toLowerCase().indexOf(q) !== -1;

    var bookingStatus = t.status ? String(t.status) : '';
    var bookingStatusMatch = bookingStatus.toLowerCase().indexOf(q) !== -1;

    return idMatch || dateMatch || statusMatch || petsMatch || priceMatch || typeMatch || bookingStatusMatch;
  });

  // View segmentation
  var now = new Date();
  var pastTransactions = filteredTransactions.filter(function (t) {
    return t && t.startTime && new Date(t.startTime) < now;
  });
  var upcomingTransactions = filteredTransactions.filter(function (t) {
    return t && t.startTime && new Date(t.startTime) >= now;
  });

  var viewList = filteredTransactions;
  if (view === 'past') viewList = pastTransactions;
  if (view === 'upcoming') viewList = upcomingTransactions;

  // Pagination
  var totalPages = Math.max(1, Math.ceil(viewList.length / itemsPerPage));
  var startIndex = (currentPage - 1) * itemsPerPage;
  var endIndex = startIndex + itemsPerPage;
  var displayedTransactions = viewList.slice(startIndex, endIndex);

  function handlePrev() {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  }
  function handleNext() {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  }

  // UI helpers
  function statusBadgeClass(s) {
    var k = toSafeLower(s);
    if (k === 'completed' || k === 'succeeded' || k === 'paid') return 'ph-badge ph-badge-success';
    if (k === 'pending' || k === 'authorized') return 'ph-badge ph-badge-warn';
    if (k === 'canceled' || k === 'voided' || k === 'failed' || k === 'refunded') return 'ph-badge ph-badge-danger';
    return 'ph-badge ph-badge-neutral';
  }

  // Render
  if (loading) {
    return (
      <div className="ph-loading">
        <div className="ph-spinner" />
        <p className="ph-loading-text">Loading payment history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ph-error">
        <p><strong>Error:</strong> {error}</p>
        <BackButton />
      </div>
    );
  }

  var allActive = view === 'all';
  var pastActive = view === 'past';
  var upcomingActive = view === 'upcoming';

  return (
    <div className="ph-page">
      <div className="ph-toolbar">
        <div className="ph-toolbar-left">
          <BackButton />
          <h2 className="ph-title">Payment History</h2>
        </div>

        <div className="ph-toolbar-right">
          <div className="ph-seg">
            <button
              className={'ph-seg-btn' + (allActive ? ' ph-seg-btn-active' : '')}
              onClick={function(){ handleToggleView('all'); }}
            >
              All
            </button>
            <button
              className={'ph-seg-btn' + (pastActive ? ' ph-seg-btn-active' : '')}
              onClick={function(){ handleToggleView('past'); }}
            >
              Past
            </button>
            <button
              className={'ph-seg-btn' + (upcomingActive ? ' ph-seg-btn-active' : '')}
              onClick={function(){ handleToggleView('upcoming'); }}
            >
              Upcoming
            </button>
          </div>

          <div className="ph-search">
            <input
              className="ph-search-input"
              type="text"
              placeholder="Search by pet, type, date, status, amount…"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      <div className="ph-list">
        {displayedTransactions.length > 0 ? (
          displayedTransactions.map(function (t) {
            var id = t && t.id ? t.id : '';
            var type = t.type === 'membership'
            ? 'Membership'
            : t.type === 'bundle'
            ? `Bundle (${t.bundleType || t.bundleLabel || ''})`
            : t.type === 'transaction'
            ? t.transactionType || 'Transaction'
            : t.bookingType || 'Booking';
            var dateText = t && t.startTime ? formatDate(t.startTime) : 'N/A';
            var petNames = 'N/A';
            if (t && t.selectedPets && Array.isArray(t.selectedPets) && t.selectedPets.length > 0) {
              petNames = t.selectedPets.map(function(p) {
                return p && p.petName ? p.petName : '';
              }).filter(Boolean).join(', ');
              if (!petNames) petNames = 'N/A';
            }
            var amountValue =
              t.type === 'membership' ? t.priceCents / 100 :
              t.type === 'bundle' ? t.amount || t.priceCents / 100 :
              t.type === 'transaction' ? t.amount :
              t.originalPrice;

            var amount = formatMoney(amountValue);
            var statusTxt =
              t.type === 'membership'
                ? t.billingStatus
                : t.type === 'bundle'
                ? t.status
                : t.transactionStatus || t.status || 'Unknown';

            return (
              <div
                key={'t-' + id + '-' + dateText}
                className="ph-item"
                onClick={function(){
                  navigate('/console/userPaymentDetails/' + userId + '/' + id, { state: { transaction: t } });
                }}
              >
                <div className="ph-item-row">
                  <div className="ph-main">
                    <div className="ph-line">
                      <span className="ph-type">{type}</span>
                      <span className="ph-dot">•</span>
                      <span className="ph-pets">{petNames}</span>
                    </div>
                    <div className="ph-sub">{dateText}</div>
                  </div>

                  <div className="ph-side">
                    <span className={statusBadgeClass(statusTxt)}>{toSafeLower(statusTxt) || 'unknown'}</span>
                    <span className="ph-amount">{amount}</span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="ph-empty">
            No {view === 'past' ? 'past' : view === 'upcoming' ? 'upcoming' : 'matching'} transactions.
          </div>
        )}
      </div>

      {displayedTransactions.length > 0 && (
        <div className="ph-pagination">
          <button className="ph-btn" disabled={currentPage === 1} onClick={handlePrev}>Previous</button>
          <div className="ph-pageinfo">Page {currentPage} of {totalPages}</div>
          <button className="ph-btn" disabled={currentPage === totalPages} onClick={handleNext}>Next</button>
        </div>
      )}
    </div>
  );
};

export default UserPaymentHistory;
